import { format, parseISO } from 'date-fns';
import React, { useMemo } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { ChartSimpleIcon } from '@revfluence/fresh-icons/solid/esm';

import { Empty } from '@/app/refresh-components';
import { Card } from '@/shadcn/components/ui/card';

import { GraphCardContent, GraphCardHeader, GraphCardTitle } from '../../../components/graph-card';
import MetricChangeIndicator from '../../../components/metric-change-indicator';
import { DateType } from '../../../types';
import { formatNumberDefault } from '../../../utils/formatNumber';

interface FollowerGrowthCardProps {
  className?: string;
  startDate?: string;
  endDate?: string;
  dateType?: DateType;
  timeSeriesData?: {
    accountFollowersBreakdown?: {
      daily?: {
        followersByDate?: Array<{
          date: string;
          count: number;
        }>;
      };
    };
  } | null;
}

// -----------------------------------------------------------------------------
// Helper — label formatter for Recharts Tooltip (kept outside component to avoid
// re-creation on every render and to provide strong typing)
// -----------------------------------------------------------------------------

interface TooltipPayloadItem {
  payload?: {
    date?: string;
  };
}

const followerGrowthLabelFormatter = (label: string | number, payload: TooltipPayloadItem[]): string => {
  const isoDate = payload?.[0]?.payload?.date;
  try {
    return isoDate ? `Date: ${format(parseISO(isoDate), 'MMM d, yyyy')}` : String(label);
  } catch {
    return String(label);
  }
};

const FollowerGrowthCard = React.forwardRef<HTMLDivElement, FollowerGrowthCardProps>(
  ({ className, startDate, endDate, dateType, timeSeriesData }, ref) => {
    // Transform time series data into chart format - simplified to just total
    const chartData = useMemo(() => {
      if (!timeSeriesData?.accountFollowersBreakdown?.daily?.followersByDate?.length) {
        return [];
      }

      // Process total follower data
      const validTotalData = timeSeriesData.accountFollowersBreakdown.daily.followersByDate
        .filter((item): item is { date: string; count: number } => {
          try {
            return (
              typeof item.date === 'string' &&
              typeof item.count === 'number' &&
              !isNaN(item.count) &&
              !isNaN(parseISO(item.date).getTime())
            );
          } catch {
            return false;
          }
        })
        .sort((a, b) => parseISO(b.date).getTime() - parseISO(a.date).getTime());

      // Deduplicate total data by date
      const uniqueTotalData = new Map<string, number>();
      validTotalData.forEach((item) => {
        if (!uniqueTotalData.has(item.date)) {
          uniqueTotalData.set(item.date, item.count);
        }
      });

      // Create simple total data structure
      return Array.from(uniqueTotalData.entries())
        .map(([date, totalCount]) => ({
          date,
          followers: totalCount,
          formattedDate: format(parseISO(date), 'MMM d'),
        }))
        .sort((a, b) => parseISO(a.date).getTime() - parseISO(b.date).getTime());
    }, [timeSeriesData]);

    // Determine if we have real data
    const hasRealData = chartData.length > 0;

    // Calculate follower growth and get first/last values for metric indicator
    const { currentFollowerGrowth, firstFollowerCount, lastFollowerCount } = useMemo(() => {
      if (!hasRealData || chartData.length < 2) {
        return { currentFollowerGrowth: 0, firstFollowerCount: 0, lastFollowerCount: 0 };
      }
      const firstDataPoint = chartData[0];
      const lastDataPoint = chartData[chartData.length - 1];
      return {
        currentFollowerGrowth: lastDataPoint.followers - firstDataPoint.followers,
        firstFollowerCount: firstDataPoint.followers,
        lastFollowerCount: lastDataPoint.followers,
      };
    }, [chartData, hasRealData]);

    // Calculate peak followers for screen reader summary
    const peakFollowers = useMemo(() => {
      if (!hasRealData) return 0;
      return Math.max(...chartData.map((data) => data.followers));
    }, [chartData, hasRealData]);

    return (
      <Card ref={ref} className={className} role="region" aria-label="Follower growth over time">
        <GraphCardHeader className="border-b">
          <GraphCardTitle>Follower Growth</GraphCardTitle>
        </GraphCardHeader>
        <GraphCardContent className="pt-4 flex flex-col justify-between flex-1">
          <div className="mb-4 ml-12">
            <div
              className="font-sans text-[32px] font-medium text-gray-900 leading-[48px] mb-1"
              aria-live="polite"
              aria-atomic="true"
            >
              {formatNumberDefault(currentFollowerGrowth)}
            </div>
            <MetricChangeIndicator
              current={lastFollowerCount}
              previous={firstFollowerCount}
              startDate={startDate}
              endDate={endDate}
              dateType={dateType}
            />
            {!hasRealData && (
              <div className="text-xs text-muted-foreground mt-2 italic">
                ⚠️ No follower growth data available for this period.
              </div>
            )}
          </div>

          <div
            className="sm:h-[200px] h-[220px]"
            role="graphics-document"
            aria-label={`Line chart showing follower growth over time. Total growth: ${formatNumberDefault(currentFollowerGrowth)}`}
          >
            <ResponsiveContainer width="100%" height="100%">
              {!hasRealData ? (
                <Empty
                  icon={ChartSimpleIcon}
                  title="No follower data available."
                  description="Follower growth data will appear here once available."
                  size="lg"
                />
              ) : (
                <LineChart data={chartData} aria-hidden="true" margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="formattedDate"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={10}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    domain={['auto', 'auto']}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={10}
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value: number) => String(formatNumberDefault(value))}
                  />
                  <Tooltip
                    labelStyle={{ color: '#374151' }}
                    formatter={(value: number) => [new Intl.NumberFormat('en-US').format(value), 'Followers']}
                    labelFormatter={followerGrowthLabelFormatter}
                  />
                  <Line
                    type="linear"
                    dataKey="followers"
                    stroke="#4F7DBA"
                    strokeWidth={4}
                    dot={false}
                    activeDot={{ r: 6, stroke: '#4F7DBA', strokeWidth: 2 }}
                    role="graphics-symbol"
                    aria-label="Follower growth trend line"
                  />
                </LineChart>
              )}
            </ResponsiveContainer>
          </div>

          {/* Screen reader accessible table */}
          <div className="sr-only">
            <table>
              <caption>Follower growth by time period</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Followers</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((data) => (
                  <tr key={data.date}>
                    <th scope="row">{data.formattedDate}</th>
                    <td>{formatNumberDefault(data.followers)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summary for screen readers */}
          <div className="sr-only" aria-live="polite">
            <h3>Follower Growth Summary:</h3>
            <ul>
              <li>Total follower growth: {formatNumberDefault(currentFollowerGrowth)}</li>
              {hasRealData && (
                <li>
                  Period start: {formatNumberDefault(firstFollowerCount)}, Period end:{' '}
                  {formatNumberDefault(lastFollowerCount)}
                </li>
              )}
              {hasRealData && <li>Peak followers: {formatNumberDefault(peakFollowers)}</li>}
            </ul>
          </div>
        </GraphCardContent>
      </Card>
    );
  },
);

FollowerGrowthCard.displayName = 'FollowerGrowthCard';

export default FollowerGrowthCard;
